import { applicationErrorMessage } from "../lib/applicationErrors.mjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminVisaRequests, AdminVerificationServices } from "@/components/AdminServiceRequests";
import VerificationOps from "@/pages/VerificationOps";
import AdminSponsorRefresh from "@/components/AdminSponsorRefresh";
import AdminJobQueue from "@/components/AdminJobQueue";
import AdminDocuments from "@/components/AdminDocuments";
import AdminOverviewTiles from "@/components/AdminOverviewTiles";
import AdminStudyCatalogue from "@/components/AdminStudyCatalogue";
import AdminCandidateDetails from "@/components/AdminCandidateDetails";
import AdminCandidateSearch from "@/components/AdminCandidateSearch";
import AdminCandidateRow from "@/components/AdminCandidateRow";
import { useAuth } from "@/context/AuthContext";
import {
  ADMIN_TABS, adminSectionId, adminTabFromSection, adminTabUrl,
  canAccessVerificationQueue,
} from "@/lib/adminNavigation";
import { useLatestValue } from "@/hooks/useLatestValue";

const CATEGORIES = ["recruitment", "career", "cv", "cv_marketing_package", "employer_recruitment",
  "education", "course", "document_verification", "visa_administrative_support",
  "regulated_immigration", "partner", "white_label_demonstration", "chargeable_technical_support"];

export default function Admin() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const tab = adminTabFromSection(searchParams.get("section"));
  const verificationAllowed = canAccessVerificationQueue(user?.roles);
  const [overview, setOverview] = useState(null);
  const [imports, setImports] = useState([]);
  const [report, setReport] = useState(null);
  const [articles, setArticles] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState("");
  const newsLoadSequence = useRef(0);
  const loadNews = useCallback(async () => {
    const sequence = ++newsLoadSequence.current;
    setNewsLoading(true); setNewsError("");
    try {
      const { data } = await api.get("/admin/news");
      if (sequence === newsLoadSequence.current) setArticles(data);
    } catch (error) {
      if (sequence === newsLoadSequence.current) setNewsError(applicationErrorMessage(error, "Unable to load newsroom articles. Please try again."));
    } finally {
      if (sequence === newsLoadSequence.current) setNewsLoading(false);
    }
  }, []);
  const invalidateNewsLoad = useCallback(() => { ++newsLoadSequence.current; }, []);
  useEffect(() => {
    if (tab === "Newsroom") loadNews();
    return invalidateNewsLoad;
  }, [tab, loadNews, invalidateNewsLoad]);
  const [newsPending, setNewsPending] = useState("");
  const [newsMessage, setNewsMessage] = useState("");
  const newsActionPending = useRef(false);
  const publishArticle = async (id) => {
    if (newsActionPending.current) return;
    newsActionPending.current = true; setNewsPending(id); setNewsMessage("");
    try {
      const { data } = await api.post(`/admin/news/${id}/decision`, { decision: "publish", channels: ["linkedin", "facebook"] });
      setArticles((rows) => rows.map((article) => article.id === id ? { ...article, status: data.status } : article));
      setNewsMessage(["Article published on the website. Social delivery depends on connected channels.", ...(data.warnings || [])].join(" "));
    } catch (error) {
      setNewsMessage(applicationErrorMessage(error, "Unable to save the publication decision. Please try again."));
    } finally { newsActionPending.current = false; setNewsPending(""); }
  };
  const [logs, setLogs] = useState({ items: [], page: 1, pages: 0, total: 0 });
  const [auditLoading, setAuditLoading] = useState(true);
  const [auditError, setAuditError] = useState("");
  const auditLoadSequence = useRef(0);
  const loadAudit = useCallback(async (page = 1) => {
    const sequence = ++auditLoadSequence.current;
    setAuditLoading(true); setAuditError("");
    try {
      const { data } = await api.get("/admin/audit-logs", { params: { page, size: 30 } });
      if (sequence === auditLoadSequence.current) setLogs(data);
    } catch (error) {
      if (sequence === auditLoadSequence.current) setAuditError(applicationErrorMessage(error, "Unable to load audit history. Please try again."));
    } finally {
      if (sequence === auditLoadSequence.current) setAuditLoading(false);
    }
  }, []);
  const invalidateAuditLoad = useCallback(() => { ++auditLoadSequence.current; }, []);
  useEffect(() => {
    if (tab === "Audit logs") loadAudit();
    return invalidateAuditLoad;
  }, [tab, loadAudit, invalidateAuditLoad]);
  const [health, setHealth] = useState(null);
  const [backgroundQueue, setBackgroundQueue] = useState([]);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState("");
  const healthLoadSequence = useRef(0);
  const [jobRetryPending, setJobRetryPending] = useState("");
  const jobRetryLock = useRef(false);
  const loadHealth = useCallback(async () => {
    const sequence = ++healthLoadSequence.current;
    setHealthLoading(true); setHealthError("");
    try {
      const [healthResponse, queueResponse] = await Promise.all([api.get("/admin/health"), api.get("/admin/queue")]);
      if (sequence === healthLoadSequence.current) {
        setHealth(healthResponse.data); setBackgroundQueue(queueResponse.data);
      }
    } catch (error) {
      if (sequence === healthLoadSequence.current) setHealthError(applicationErrorMessage(error, "Unable to refresh system health and background jobs. Please try again."));
    } finally {
      if (sequence === healthLoadSequence.current) setHealthLoading(false);
    }
  }, []);
  const invalidateHealthLoad = useCallback(() => { ++healthLoadSequence.current; }, []);
  useEffect(() => {
    if (tab === "System health") loadHealth();
    return invalidateHealthLoad;
  }, [tab, loadHealth, invalidateHealthLoad]);

  const [flags, setFlags] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [sourceForm, setSourceForm] = useState({ name: "", source_url: "", data_type: "jobs", coverage: "", licence_status: "review_required", refresh_frequency: "manual" });
  const [catalogType, setCatalogType] = useState("jobs");
  const [catalogSourceId, setCatalogSourceId] = useState("");
  const [catalogJson, setCatalogJson] = useState("[]");
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState("");
  const [sourcePreview, setSourcePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [documentPreviewFile, setDocumentPreviewFile] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [programmeCases, setProgrammeCases] = useState([]);
  const [studyApplications, setStudyApplications] = useState([]);
  const [payments, setPayments] = useState(null);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState("");
  const paymentLoadVersion = useRef(0);
  const loadPayments = useCallback(async (page = 1) => {
    const version = ++paymentLoadVersion.current;
    setPaymentsLoading(true); setPaymentsError("");
    try {
      const { data } = await api.get("/admin/payments", { params: { page, page_size: 50 } });
      if (version === paymentLoadVersion.current) setPayments(data);
    } catch (error) {
      if (version === paymentLoadVersion.current) setPaymentsError(error?.response?.data?.detail || "Unable to load payments. Try again.");
    } finally {
      if (version === paymentLoadVersion.current) setPaymentsLoading(false);
    }
  }, []);
  const invalidatePaymentLoad = useCallback(() => { ++paymentLoadVersion.current; }, []);
  useEffect(() => {
    if (tab === "Payments") loadPayments();
    return invalidatePaymentLoad;
  }, [tab, loadPayments, invalidatePaymentLoad]);
  const [paymentRetry, setPaymentRetry] = useState("");
  const [paymentFeedback, setPaymentFeedback] = useState("");
  const paymentRetryPending = useRef(false);
  const canRetryPayments = (user?.roles || []).some((role) => ["finance_admin", "super_admin"].includes(role));
  const retryPaymentFulfilment = async (payment) => {
    if (paymentRetryPending.current) return;
    paymentRetryPending.current = true;
    setPaymentRetry(payment.session_id); setPaymentFeedback("");
    try {
      const { data } = await api.post(`/admin/payments/${encodeURIComponent(payment.session_id)}/retry-fulfilment`);
      setPayments((current) => current ? { ...current, items: current.items.map((item) =>
        item.session_id === payment.session_id ? { ...item, fulfilment_status: data.fulfilment_status } : item) } : current);
      setPaymentFeedback(`Service fulfilment completed for ${payment.session_id}. No new charge was created.`);
    } catch (error) {
      setPaymentFeedback(error?.response?.data?.detail || "Unable to retry service fulfilment. Payment remains confirmed; contact support.");
      try { setPayments((await api.get("/admin/payments", { params: { page: payments?.page || 1, page_size: 50 } })).data); } catch (_) { /* Keep the existing table and action feedback. */ }
    } finally {
      paymentRetryPending.current = false; setPaymentRetry("");
    }
  };
  const [refundPayment, setRefundPayment] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundSaving, setRefundSaving] = useState(false);
  const recordRefundRequest = async (event) => {
    event.preventDefault();
    if (!refundPayment || paymentRetryPending.current || !canRetryPayments) return;
    paymentRetryPending.current = true; setRefundSaving(true); setPaymentFeedback("");
    const sessionId = refundPayment.session_id;
    try {
      const { data } = await api.post("/payments/refunds", { session_id: sessionId, reason: refundReason.trim() });
      setPayments((current) => current ? { ...current, items: current.items.map((item) =>
        item.session_id === sessionId ? { ...item, refund_requested: true,
          refund_status: data.refund_status || "requested_pending_provider" } : item) } : current);
      setPaymentFeedback(`${data.note || "Refund request recorded."} ${data.warning || ""} Reference: ${sessionId}`);
      setRefundPayment(null); setRefundReason("");
    } catch (error) {
      setPaymentFeedback(error?.response?.data?.detail || "Unable to confirm the refund request. Refresh the transaction before trying again.");
      try { setPayments((await api.get("/admin/payments", { params: { page: payments?.page || 1, page_size: 50 } })).data); } catch (_) { /* Retain the entered reason and feedback. */ }
    } finally {
      paymentRetryPending.current = false; setRefundSaving(false);
    }
  };
  const [candidates, setCandidates] = useState([]);
  const [candidateLoading, setCandidateLoading] = useState(true);
  const [candidateSearch, setCandidateSearch] = useState("");
  const candidateSearchRef = useLatestValue(candidateSearch);
  const [candidateCity, setCandidateCity] = useState("");
  const candidateCityRef = useLatestValue(candidateCity);
  const [candidateCountry, setCandidateCountry] = useState("");
  const candidateCountryRef = useLatestValue(candidateCountry);
  const [candidateActiveFilters, setCandidateActiveFilters] = useState({});
  const [hireflow, setHireflow] = useState(null);
  const [hireflowSync, setHireflowSync] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateEdit, setCandidateEdit] = useState({});
  const [reviewAction, setReviewAction] = useState(null);
  const [programmeReview, setProgrammeReview] = useState(null);
  const [programmeReviewHistory, setProgrammeReviewHistory] = useState([]);
  const [programmeReviewNote, setProgrammeReviewNote] = useState("");
  const [programmeReviewSaving, setProgrammeReviewSaving] = useState(false);
  const [programmeStatusFilter, setProgrammeStatusFilter] = useState("all");
  const [reviewNote, setReviewNote] = useState("");
  const [reviewSaving, setReviewSaving] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const reviewPending = useRef(false);
  useEffect(() => { setReviewError(""); }, [reviewAction]);
  const [extra, setExtra] = useState({});
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [templateBusy, setTemplateBusy] = useState(false);
  const templateActionLock = useRef(false);
  const templateLoadSequence = useRef(0);
  const templateDirtyKeys = useRef(new Set());
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ key: "", label: "", channel: "whatsapp", body: "" });
  const [form, setForm] = useState({
    name: "", title: "", biography: "", languages: "English", categories: ["career"],
    qualifications: "", regulatory_details: "", meeting_modes: ["google_meet"],
    durations: "30,60", price_pence: 9000, max_daily_bookings: 6, active: true,
  });
  const [msg, setMsg] = useState("");
  const fileRef = useRef();

  const load = useCallback(async () => {
    const resources = [
      ["/admin/overview", setOverview],
      ["/sponsors/imports", setImports],
      ["/admin/feature-flags", setFlags],
      ["/admin/consultants", setConsultants],
      ["/admin/data-sources", setDataSources],
      ["/staff/study/applications", setStudyApplications],
      ["/integrations/hireflow/admin/links", setHireflow],
      ["/integrations/hireflow/admin/sync-status", setHireflowSync],
    ];

    const requests = resources.map(async ([url, setter]) => {
      const response = await api.get(url);
      setter(response.data);
      return response;
    });

    const results = await Promise.allSettled(requests);
    const failed = results.filter((item) => item.status === "rejected");

    if (failed.length === results.length) {
      setMsg("Administrator access required.");
    } else {
      setMsg("");
    }
  }, []);
  const loadProgrammeCases = useCallback(async () => {
    try {
      setProgrammeCases((await api.get("/staff/programme-cases")).data);
      setMsg("");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Unable to load programme review cases.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const retryHireflowSync = async (itemId) => {
    try {
      await api.post(`/integrations/hireflow/admin/sync-status/${itemId}/retry`);
      setHireflowSync((current) => ({ ...current, items: current.items.map((item) => item.id === itemId ? { ...item, status: "pending", last_error: null } : item) }));
      setMsg("HireFlow retry queued.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to queue the retry."); }
  };

  const retryBackgroundJob = async (jobId) => {
    if (jobRetryLock.current) return;
    jobRetryLock.current = true; setJobRetryPending(jobId);
    invalidateHealthLoad(); setHealthLoading(false);
    try {
      const { data } = await api.post(`/admin/queue/${jobId}/retry`);
      setBackgroundQueue((current) => current.map((item) => item.id === jobId
        ? { ...item, status: "queued", attempts: 0, last_error: null } : item));
      setMsg(["Background job safely queued for retry.", ...(data.warnings || [])].join(" "));
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to retry the background job."); }
    finally { jobRetryLock.current = false; setJobRetryPending(""); }
  };

  const openCandidate = async (candidateId) => { try { const data = (await api.get(`/staff/candidates/${candidateId}`)).data; setSelectedCandidate(data); setCandidateEdit(Object.fromEntries(["full_name", "email", "phone", "whatsapp", "country", "city"].map((key) => [key, data.candidate[key] || ""]))); } catch (e) { setMsg(e?.response?.data?.detail || "Unable to load candidate details."); } };
  const refreshCandidate = async (saved, reload = false) => { if (reload) return openCandidate(selectedCandidate.candidate.id); setSelectedCandidate((current) => ({ ...current, candidate: { ...current.candidate, ...saved } })); loadCandidates(); };
  const loadCandidates = useCallback(async () => { setCandidateLoading(true); try { const params = { q: candidateSearchRef.current.trim() || undefined, city: candidateCityRef.current.trim() || undefined, country: candidateCountryRef.current.trim() || undefined }; setCandidates((await api.get("/staff/candidates", { params })).data.items || []); setCandidateActiveFilters(Object.fromEntries(Object.entries(params).filter(([, value]) => value))); } catch (e) { setMsg(e?.response?.data?.detail || "Unable to load candidates."); } finally { setCandidateLoading(false); } }, [candidateCityRef, candidateCountryRef, candidateSearchRef]);
  const openProgrammeReview = async (caseId) => {
    try {
      const [pack, history] = await Promise.all([
        api.get(`/staff/programme-cases/${caseId}/review-pack`),
        api.get(`/staff/programme-cases/${caseId}/document-history`),
      ]);
      setProgrammeReview(pack.data);
      setProgrammeReviewHistory(history.data || []);
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to load programme review pack."); }
  };

  const updateDocumentRequirement = async (requirementId, status) => {
    try {
      const { data } = await api.patch(`/staff/programme-document-requirements/${requirementId}`, { status, note: "" });
      setProgrammeReview((current) => current ? { ...current, document_requirements: (current.document_requirements || []).map((item) => item.id === requirementId ? { ...item, ...data } : item) } : current);
      setMsg("Document requirement status updated.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to update document requirement."); }
  };

  const downloadProgrammeDocument = async (file) => {
    try {
      const response = await api.get(`/files/${file.id}/download`, { responseType: "blob" });
      const href = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = file.original_filename || "programme-document";
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to open programme document."); }
  };

  const updateProgrammeReview = async (status) => {
    if (!programmeReview) return;
    if (status === "request_information" && programmeReviewNote.trim().length < 5) { setMsg("Add a clear information request before saving."); return; }
    setProgrammeReviewSaving(true);
    try {
      await api.patch(`/staff/programme-cases/${programmeReview.case_id}`, { status, note: programmeReviewNote.trim() });
      setMsg(`Programme case marked: ${status.replaceAll("_", " ")}.`);
      setProgrammeReview(null); setProgrammeReviewNote(""); load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to update programme case."); }
    finally { setProgrammeReviewSaving(false); }
  };

  const clearCandidateSearch = () => { setCandidateSearch(""); setCandidateCity(""); setCandidateCountry(""); candidateSearchRef.current = ""; candidateCityRef.current = ""; candidateCountryRef.current = ""; loadCandidates(); };

  const editMessageTemplate = (key, changes) => {
    if (templateActionLock.current) return;
    templateDirtyKeys.current.add(key);
    templateLoadSequence.current++; setTemplatesLoading(false);
    setMessageTemplates((items) => items.map((item) => item.key === key ? { ...item, ...changes } : item));
  };
  const loadMessageTemplates = useCallback(async () => {
    if (templateActionLock.current) return;
    if (templateDirtyKeys.current.size && !window.confirm("Refresh templates and discard unsaved template edits? Save your changes first to keep them.")) return;
    const sequence = ++templateLoadSequence.current;
    setTemplatesLoading(true);
    try {
      const { data } = await api.get("/communications/templates");
      if (sequence === templateLoadSequence.current) {
        templateDirtyKeys.current.clear();
        setMessageTemplates(data.items || []);
      }
    } catch (e) {
      if (sequence === templateLoadSequence.current) setMsg(applicationErrorMessage(e, "Unable to load message templates."));
    } finally {
      if (sequence === templateLoadSequence.current) setTemplatesLoading(false);
    }
  }, []);

  const createMessageTemplate = async () => {
    if (templateActionLock.current) return;
    const key = newTemplate.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    if (!key || !newTemplate.label.trim() || newTemplate.body.trim().length < 5) { setMsg("Enter a template name and message before saving."); return; }
    if (messageTemplates.some((item) => item.key === key)) {
      setMsg("A template with this name already exists. Edit the existing template or choose a different name.");
      return;
    }
    templateActionLock.current = true;
    setTemplateBusy(true); setMsg("");
    try {
      const { data } = await api.put(`/communications/templates/${key}`, { label: newTemplate.label.trim(), channel: newTemplate.channel, body: newTemplate.body.trim(), active: true }, { params: { create_only: true } });
      templateLoadSequence.current++; setTemplatesLoading(false);
      setMessageTemplates((items) => [...items.filter((item) => item.key !== data.key), data]);
      setMsg(data.warning || "New message template created and added to the list.");
      setNewTemplate({ key: "", label: "", channel: "whatsapp", body: "" });
    } catch (e) { setMsg(applicationErrorMessage(e, "Unable to create message template.")); } finally {
      templateActionLock.current = false;
      setTemplateBusy(false);
    }
  };
  useEffect(() => {
    if (tab === "Message Templates") loadMessageTemplates();
    if (tab === "Candidates") loadCandidates();
    if (tab === "Programme reviews") loadProgrammeCases();
  }, [tab, loadCandidates, loadMessageTemplates, loadProgrammeCases]);

  const deleteMessageTemplate = async (template) => {
    if (templateActionLock.current) return;
    if (template.is_default) { setMsg("Default templates cannot be deleted. Edit them instead."); return; }
    templateActionLock.current = true;
    setTemplateBusy(true); setMsg("");
    try {
      const { data } = await api.post(`/communications/templates/${template.key}/delete`);
      templateLoadSequence.current++; setTemplatesLoading(false);
      templateDirtyKeys.current.delete(template.key);
      setMessageTemplates((items) => data.restored_template
        ? items.map((item) => item.key === template.key ? data.restored_template : item)
        : items.filter((item) => item.key !== template.key));
      setMsg([data.restored_template ? `${template.label} customisation removed. Built-in template restored.` : `${template.label} template deleted.`, data.warning].filter(Boolean).join(" "));
    } catch (e) { setMsg(applicationErrorMessage(e, "Unable to delete message template.")); } finally {
      templateActionLock.current = false;
      setTemplateBusy(false);
    }
  };
  const saveMessageTemplate = async (template) => {
    if (templateActionLock.current) return;
    templateActionLock.current = true;
    setTemplateBusy(true); setMsg("");
    try {
      const { data } = await api.put(`/communications/templates/${template.key}`, { label: template.label, channel: template.channel, body: template.body, active: template.active !== false });
      templateLoadSequence.current++; setTemplatesLoading(false);
      templateDirtyKeys.current.delete(template.key);
      setMessageTemplates((items) => items.map((item) => item.key === data.key ? data : item));
      setMsg(data.warning || `${template.label} template saved.`);
    } catch (e) { setMsg(applicationErrorMessage(e, "Unable to save message template.")); } finally {
      templateActionLock.current = false;
      setTemplateBusy(false);
    }
  };
  const createConsultant = async () => {
    try {
      await api.post("/admin/consultants", {
        ...form,
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
        qualifications: form.qualifications.split(",").map((s) => s.trim()).filter(Boolean),
        durations: form.durations.split(",").map((s) => Number(s.trim())).filter(Boolean),
        price_pence: Number(form.price_pence),
        regulatory_details: form.regulatory_details || null,
      });
      setMsg("Consultant profile published. It is now bookable as a paid session.");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Unable to create the consultant profile."); }
  };

  const createDataSource = async () => {
    setMsg("");
    const data = {
      ...sourceForm,
      coverage: sourceForm.coverage.split(",").map((s) => s.trim()).filter(Boolean),
    };

    try {
      const response = await api.post("/admin/data-sources", data);
      const createdSource = response.data;

      setDataSources((current) => [
        createdSource,
        ...current.filter((item) => item.id !== createdSource.id),
      ]);

      setSourceForm({
        name: "",
        source_url: "",
        data_type: "jobs",
        coverage: "",
        licence_status: "review_required",
        refresh_frequency: "manual",
      });

      setMsg("Data source registered successfully.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      setMsg(detail || `Unable to register data source${status ? ` (HTTP ${status})` : ""}.`);
    }
  };

  const toggleJobSourceSchedule = async (source) => {
    try {
      await api.patch(`/admin/data-sources/${source.id}/refresh-schedule`, { enabled: source.refresh_frequency !== "daily" });
      await load();
      setMsg("Vacancy source schedule updated. New imports still require approval.");
    } catch (error) { setMsg(error?.response?.data?.detail || "Unable to update source checks."); }
  };

  const previewStudySource = async () => {
    if (!catalogSourceId) {
      setMsg("Select a registered source first.");
      return;
    }
    if (!sourcePreviewUrl.trim()) {
      setMsg("Enter an official source URL.");
      return;
    }

    setPreviewLoading(true);
    setSourcePreview(null);

    try {
      const { data } = await api.post(`/admin/data-imports/${catalogType}/preview-url`, {
        source_id: catalogSourceId,
        source_url: sourcePreviewUrl.trim(),
      });

      setSourcePreview(data);
      setMsg("Preview extracted. Review the details before importing.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Unable to preview this source URL.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const previewStudyDocument = async () => {
    if (!documentPreviewFile) {
      setMsg("Choose a PDF, Word document, or text file first.");
      return;
    }

    setDocumentPreviewLoading(true);
    setDocumentPreview(null);

    try {
      const formData = new FormData();
      formData.append("file", documentPreviewFile);

      const { data } = await api.post(
        "/admin/data-imports/study/preview-document",
        formData,
      );

      setDocumentPreview(data);
      setMsg("Document extracted. Review the evidence before importing.");
    } catch (e) {
      setMsg(
        e?.response?.data?.detail ||
        "Unable to extract this document.",
      );
    } finally {
      setDocumentPreviewLoading(false);
    }
  };

  const useDocumentProgrammeCandidates = () => {
    const candidates = documentPreview?.programme_candidates || [];
    const source = dataSources.find((item) => item.id === catalogSourceId);

    if (!candidates.length) {
      setMsg("No programme records were detected in this document.");
      return;
    }

    const country = Array.isArray(source?.coverage)
      ? source.coverage[0] || ""
      : source?.country || "";

    const items = candidates.map((item) => ({
      title: item.title || "",
      provider: source?.name || "",
      country,
      source_url: source?.source_url || "",
      description: "",
      level: item.level || "",
      duration: "",
      study_mode: "",
      application_deadline: "",
      intake_dates: "",
      eligibility: "",
      tuition_fee: "",
      extraction_status: "review_required",
      source_evidence: item.source_evidence || "",
    }));

    setCatalogJson(JSON.stringify(items, null, 2));
    setMsg("Detected programmes copied for review. Verify every field before importing.");
  };

  const useSourcePreview = () => {
    if (!sourcePreview) return;

    const items =
      sourcePreview.items ||
      sourcePreview.courses ||
      sourcePreview.records ||
      [sourcePreview];

    setCatalogJson(JSON.stringify(items, null, 2));
    setMsg("Preview copied into the JSON editor. Check all fields before importing.");
  };

  const importCatalogue = async () => {
    try {
      const items = JSON.parse(catalogJson);
      if (!Array.isArray(items) || !items.length) throw new Error("Enter a non-empty JSON array.");

      const uniqueItems = [];
      const seenItems = new Set();

      for (const item of items) {
        const key = [
          item.provider || item.institution_name || "",
          item.title || item.programme_name || "",
          item.country || "",
        ]
          .join("|")
          .toLowerCase()
          .replace(/[^a-z0-9|]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();

        if (seenItems.has(key)) continue;
        seenItems.add(key);
        uniqueItems.push(item);
      }

      const endpoint = catalogType === "jobs" ? "/admin/data-imports/jobs" : "/admin/data-imports/study";
      const data = await api.post(endpoint, {
        source_id: catalogSourceId,
        items: uniqueItems,
      });
      setMsg("Catalogue batch created as pending approval.");
      setCatalogJson("[]");
      load();
      return data;
    } catch (e) { setMsg(e?.response?.data?.detail || e.message || "Catalogue import failed."); }
  };

  const upload = async () => {
    const f = fileRef.current.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/sponsors/imports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      setMsg(`Import validated: ${data.record_count} organisations, ${data.duplicates} duplicates detected. ${data.requires_admin_approval ? "Abnormal change volume — administrator approval required." : ""}`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Import rejected."); }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 py-10" data-testid="admin-page">
      <p className="overline">EuropEdu control centre</p>
      <h1 className="mt-3 text-3xl font-semibold">Administration</h1>
      {msg && <p data-testid="admin-message" className="mt-3 text-sm text-slate-800">{msg}</p>}

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {ADMIN_TABS.map((t) => (
          <Link key={t} to={adminTabUrl(t)} data-testid={`admin-tab-${adminSectionId(t)}`}
            aria-current={tab === t ? "page" : undefined}
            className={`pill transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 active:scale-95 ${tab === t ? "bg-[#1e3a8a] text-white" : "bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-900"}`}>{t}</Link>
        ))}
      </div>

      {tab === "HireFlow" && (
        <div className="mt-8 space-y-6" data-testid="admin-hireflow">
        <section className="card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">HireFlow candidate links</h2><p className="mt-2 text-sm text-slate-600">Read-only view of server-managed candidate mappings and integration readiness.</p></div><span className={`pill ${hireflow === null ? "bg-slate-100 text-slate-700" : hireflow.configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{hireflow === null ? "Checking configuration…" : hireflow.configured ? "Configured" : "Not configured"}</span></div>
          <div className="mt-5 overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">Candidate</th><th>Email</th><th>HireFlow ID</th><th>Last updated</th></tr></thead><tbody>{(hireflow?.items || []).map((item) => <tr key={item.id} className="border-t border-slate-200"><td className="py-3">{item.candidate?.full_name || "Not recorded"}</td><td>{item.candidate?.email || "Not recorded"}</td><td>{item.hireflow_candidate_id}</td><td>{item.updated_at ? new Date(item.updated_at).toLocaleString("en-GB") : "Not recorded"}</td></tr>)}</tbody></table>{hireflow === null ? <p className="mt-4 text-sm text-slate-500">Loading candidate mappings…</p> : !(hireflow.items || []).length && <p className="mt-4 text-sm text-slate-500">No HireFlow candidate mappings recorded.</p>}</div>
        </section>
        <section className="card p-6" data-testid="admin-hireflow-sync-status">
          <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">Integration status</h2><p className="mt-2 text-sm text-slate-600">Tenant-scoped delivery queue for jobs, prospects and applications.</p></div><span className={`pill ${hireflowSync === null ? "bg-slate-100 text-slate-700" : hireflowSync.enabled ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{hireflowSync === null ? "Checking write status…" : hireflowSync.enabled ? "Writes enabled" : "Writes disabled"}</span></div>
          <div className="mt-5 overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">Resource</th><th>EuropEdu ID</th><th>HireFlow ID</th><th>Status</th><th>Last attempt</th><th>Error</th><th>Action</th></tr></thead><tbody>{(hireflowSync?.items || []).map((item) => <tr key={item.id} className="border-t border-slate-200"><td className="py-3 capitalize">{item.resource_type}</td><td>{item.europedu_id}</td><td>{item.hireflow_id || "—"}</td><td className="capitalize">{item.status}</td><td>{item.last_attempt_at ? new Date(item.last_attempt_at).toLocaleString("en-GB") : "Not attempted"}</td><td className="max-w-xs truncate" title={item.last_error || ""}>{item.last_error || "—"}</td><td>{["failed", "conflict"].includes(item.status) && <button type="button" className="btn-secondary" onClick={() => retryHireflowSync(item.id)}>Retry</button>}</td></tr>)}</tbody></table>{hireflowSync === null ? <p className="mt-4 text-sm text-slate-500">Loading synchronization status…</p> : !(hireflowSync.items || []).length && <p className="mt-4 text-sm text-slate-500">No HireFlow sync items recorded.</p>}</div>
        </section>
        </div>
      )}

      {tab === "DOCUMENTS" && <AdminDocuments />}
      {tab === "Visa requests" && <AdminVisaRequests />}
      {tab === "Verification services" && <AdminVerificationServices />}
      {tab === "Study catalogue" && <AdminStudyCatalogue />}

      {tab === "Overview" && overview && (
        <AdminOverviewTiles overview={overview} />
      )}

      {tab === "Approvals" && <AdminJobQueue />}

      {tab === "Sponsor imports" && (
        <section className="mt-8 space-y-6" data-testid="admin-sponsor-imports">
          <AdminSponsorRefresh roles={user?.roles || []} onImported={(version) => { setReport(version); load(); }} />
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Upload a register file (CSV)</h2>
            <input data-testid="sponsor-file-input" ref={fileRef} type="file" accept=".csv" className="field mt-3" />
            <button data-testid="sponsor-upload-button" onClick={upload} className="btn btn-primary mt-3">Validate and preview changes</button>
            <p className="mt-2 text-xs text-slate-500">Malformed files are rejected. The original file, checksum, row count and column mapping are preserved.</p>
          </div>

          {report && (
            <div className="card p-6" data-testid="import-change-report">
              <h2 className="text-lg font-semibold">Change report</h2>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {Object.entries(report.change_report.counts).map(([k, v]) => (
                  <span key={k} className="pill bg-slate-100 text-slate-700" data-testid={`count-${k}`}>{k.replace(/_/g, " ")}: {v}</span>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">{report.change_report.wording_note}</p>
              <button data-testid="publish-import-button" className="btn btn-primary mt-4"
                onClick={async () => { await api.post(`/sponsors/imports/${report.id}/publish`); setMsg("Register version published."); setReport(null); load(); }}>
                Confirm and publish this version
              </button>
            </div>
          )}

          <div className="card p-6">
            <h2 className="text-lg font-semibold">Import history</h2>
            <ul className="mt-3 text-sm space-y-1">
              {imports.map((v) => (
                <li key={v.id} className="flex justify-between gap-3">
                  <span>{v.file_name} · {v.record_count} records · {new Date(v.created_at).toLocaleString("en-GB")}</span>
                  <div className="flex gap-2"><span className="pill bg-slate-100 text-slate-700">{v.status}</span>
                  {v.status === "awaiting_approval" && <button className="btn btn-ghost" onClick={() => setReport(v)}>Review changes</button>}</div>
                </li>
              ))}
              {imports.length === 0 && (
                <li className="text-slate-500">
                  No sponsor-register imports have been recorded.
                </li>
              )}
            </ul>
          </div>
        </section>
      )}

      {tab === "Data sources" && (
        <section className="mt-8 grid gap-6 lg:grid-cols-2" data-testid="admin-data-sources">
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Register an approved source</h2>
            <p className="mt-2 text-xs text-slate-500">Record the official or licensed source before importing. Unapproved sources are blocked by the backend.</p>
            <input className="field mt-4" placeholder="Source name" value={sourceForm.name} onChange={(e) => setSourceForm({ ...sourceForm, name: e.target.value })} />
            <input className="field mt-2" placeholder="Official source URL" value={sourceForm.source_url} onChange={(e) => setSourceForm({ ...sourceForm, source_url: e.target.value })} />
            <input className="field mt-2" placeholder="Coverage, comma separated (UK, Australia...)" value={sourceForm.coverage} onChange={(e) => setSourceForm({ ...sourceForm, coverage: e.target.value })} />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <select className="field" value={sourceForm.data_type} onChange={(e) => setSourceForm({ ...sourceForm, data_type: e.target.value })}><option value="jobs">Jobs</option><option value="sponsorship">Sponsorship jobs</option><option value="study">Study</option></select>
              <select className="field" value={sourceForm.licence_status} onChange={(e) => setSourceForm({ ...sourceForm, licence_status: e.target.value })}><option value="review_required">Review required</option><option value="approved">Approved</option><option value="restricted">Restricted</option></select>
            </div>
            <label className="mt-3 block text-sm">Source checks
              <select className="field mt-1" value={sourceForm.refresh_frequency} onChange={(e) => setSourceForm({ ...sourceForm, refresh_frequency: e.target.value })}>
                <option value="manual">Manual only</option><option value="daily">Daily (approved vacancy sources)</option>
              </select>
            </label>
            <p className="mt-2 text-xs text-slate-500">Daily vacancy checks use the registered URL and need structured JobPosting data. Imported vacancies still require publication approval.</p>
            <button className="btn btn-primary mt-4" onClick={createDataSource}>Register source</button>
          </div>
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Registered sources ({dataSources.length})</h2>
            <ul className="mt-3 text-sm space-y-2">
              {dataSources.map((s) => (
                <li key={s.id} className="border-t border-slate-200 pt-2">
                  <strong>{s.name}</strong><br />
                  <span className="text-xs text-slate-500">
                    {s.data_type} · {s.licence_status} · {s.source_url}
                  </span>
                  {s.licence_status === "approved" && ["jobs", "sponsorship"].includes(s.data_type) && <div className="mt-2"><span className="text-xs">Checks: {s.refresh_frequency || "manual"}</span><button className="btn btn-ghost ml-2" onClick={() => toggleJobSourceSchedule(s)}>{s.refresh_frequency === "daily" ? "Disable daily checks" : "Enable daily checks"}</button></div>}
                </li>
              ))}
              {dataSources.length === 0 && (
                <li className="text-slate-500">
                  No approved data sources have been registered.
                </li>
              )}
            </ul>
          </div>
          <div className="card p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold">Import catalogue JSON</h2>
            <p className="mt-2 text-xs text-slate-500">Use this for validated source data. Jobs and study imports are created as pending approval; they are not public until approved.</p>
            <div className="grid gap-2 md:grid-cols-2 mt-4">
              <select className="field" value={catalogType} onChange={(e) => { setCatalogType(e.target.value); setCatalogSourceId(""); setSourcePreview(null); setCatalogJson("[]"); }}><option value="jobs">Jobs</option><option value="study">Study courses</option></select>
              <select className="field" value={catalogSourceId} onChange={(e) => { setCatalogSourceId(e.target.value); setSourcePreview(null); }}><option value="">Select registered source</option>{dataSources.filter((s) => catalogType === "jobs" ? ["jobs", "sponsorship"].includes(s.data_type) : s.data_type === "study").map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            </div>
            {catalogType === "jobs" && (
              <div className="mt-4 rounded border border-blue-100 bg-blue-50 p-4">
                <p className="text-sm font-semibold">Preview an approved vacancy source</p>
                <p className="mt-1 text-xs text-slate-600">Use a job page with structured vacancy data. Expired and incomplete records are excluded; sponsorship stays unconfirmed until reviewed.</p>
                <input aria-label="Official vacancy source URL" className="field mt-3" placeholder="https://employer.example/careers/vacancy" value={sourcePreviewUrl} onChange={(e) => { setSourcePreviewUrl(e.target.value); setSourcePreview(null); }} />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="btn btn-primary" onClick={previewStudySource} disabled={previewLoading}>{previewLoading ? "Checking source…" : "Preview vacancy URL"}</button>
                  {sourcePreview?.items?.length > 0 && <button className="btn btn-ghost" onClick={useSourcePreview}>Copy preview to JSON editor</button>}
                </div>
                {sourcePreview && <pre className="mt-3 max-h-80 overflow-auto rounded bg-white p-3 text-xs">{JSON.stringify(sourcePreview, null, 2)}</pre>}
              </div>
            )}
            {catalogType === "study" && (
              <div className="mt-4 rounded border border-blue-100 bg-blue-50 p-4">
                <p className="text-sm font-semibold">Preview an official study source</p>
                <p className="mt-1 text-xs text-slate-600">
                  Enter an official course page or course-list page. Nothing is imported or published until you review it.
                </p>

                <input
                  data-testid="study-source-preview-url"
                  className="field mt-3"
                  placeholder="https://university.example/course-list"
                  value={sourcePreviewUrl}
                  onChange={(e) => setSourcePreviewUrl(e.target.value)}
                />

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    data-testid="preview-study-source-button"
                    className="btn btn-primary"
                    onClick={previewStudySource}
                    disabled={previewLoading}
                  >
                    {previewLoading ? "Checking source..." : "Preview official URL"}
                  </button>

                  {sourcePreview && (
                    <button
                      data-testid="use-study-source-preview-button"
                      className="btn btn-ghost"
                      onClick={useSourcePreview}
                    >
                      Copy preview to JSON editor
                    </button>
                  )}
                </div>

                {sourcePreview && (
                  <pre
                    data-testid="study-source-preview"
                    className="mt-3 max-h-80 overflow-auto rounded bg-white p-3 text-xs"
                  >
                    {JSON.stringify(sourcePreview, null, 2)}
                  </pre>
                )}
                <div className="mt-5 border-t border-blue-200 pt-4">
                  <p className="text-sm font-semibold">
                    Preview a university brochure or programme document
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    Upload a PDF, Word document, or text file. The file is scanned and
                    extracted for review only. Nothing is stored or imported.
                  </p>

                  <input
                    data-testid="study-document-preview-file"
                    className="field mt-3"
                    type="file"
                    accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                    onChange={(e) => {
                      setDocumentPreviewFile(e.target.files?.[0] || null);
                      setDocumentPreview(null);
                    }}
                  />

                  <button
                    data-testid="preview-study-document-button"
                    className="btn btn-primary mt-3"
                    onClick={previewStudyDocument}
                    disabled={documentPreviewLoading}
                  >
                    {documentPreviewLoading
                      ? "Scanning and extracting..."
                      : "Preview document"}
                  </button>

                  {documentPreview?.programme_candidates?.length > 0 && (
                  <div
                    data-testid="study-programme-candidates"
                    className="mt-3 rounded border border-amber-200 bg-amber-50 p-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold">
                        Detected programmes requiring review (
                        {documentPreview.programme_candidates.length})
                      </p>
                      <button
                        data-testid="copy-study-programmes-button"
                        className="btn btn-primary"
                        onClick={useDocumentProgrammeCandidates}
                      >
                        Copy programmes to JSON editor
                      </button>
                    </div>

                    <div className="mt-3 max-h-72 overflow-auto rounded bg-white p-3">
                      {documentPreview.programme_candidates.map((item, index) => (
                        <div
                          key={`${item.level}-${item.title}-${index}`}
                          className="border-b border-slate-100 py-2 last:border-0"
                        >
                          <p className="text-sm font-medium">{item.title}</p>
                          <p className="text-xs text-slate-500">
                            {item.level || "Programme"} · Review required
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {documentPreview && (
                    <div
                      data-testid="study-document-preview"
                      className="mt-3 space-y-3 rounded bg-white p-3 text-xs"
                    >
                      <div className="grid gap-2 sm:grid-cols-3">
                        <div>
                          <strong>File</strong>
                          <div>{documentPreview.filename}</div>
                        </div>
                        <div>
                          <strong>Characters</strong>
                          <div>{documentPreview.character_count}</div>
                        </div>
                        <div>
                          <strong>Status</strong>
                          <div>{documentPreview.extraction_status}</div>
                        </div>
                      </div>

                      {documentPreview.headings?.length > 0 && (
                        <div>
                          <strong>Detected sections</strong>
                          <div className="mt-1">
                            {documentPreview.headings.join(" · ")}
                          </div>
                        </div>
                      )}

                      {documentPreview.evidence &&
                        Object.keys(documentPreview.evidence).length > 0 && (
                          <div>
                            <strong>Detected field evidence</strong>
                            <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap">
                              {JSON.stringify(
                                documentPreview.evidence,
                                null,
                                2,
                              )}
                            </pre>
                          </div>
                        )}

                      <div>
                        <strong>Extracted text</strong>
                        <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap">
                          {documentPreview.text_preview}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <textarea className="field mt-3 font-mono text-xs" rows={10} value={catalogJson} onChange={(e) => setCatalogJson(e.target.value)} />
            <button className="btn btn-primary mt-3" onClick={importCatalogue}>Validate and import batch</button>
          </div>
        </section>
      )}

      {tab === "Newsroom" && (
        <section className="mt-8 card p-6" data-testid="admin-newsroom">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Drafts and approvals</h2>
            <button className="btn btn-ghost" disabled={newsLoading || !!newsPending} onClick={loadNews}>
              {newsLoading ? "Loading articles…" : "Refresh articles"}
            </button>
          </div>
          {newsError && <p role="alert" className="mt-3 text-sm text-red-700">{newsError}</p>}
          {newsLoading && <p role="status" className="mt-3 text-sm text-slate-600">Loading newsroom articles…</p>}
          {newsMessage && <p role="status" className="mt-3 text-sm text-slate-700">{newsMessage}</p>}
          {articles.map((a) => (
            <div key={a.id} className="border-t border-slate-200 py-4" data-testid={`article-${a.id}`}>
              <p className="font-semibold">{a.headline}</p>
              <p className="text-sm text-slate-600 mt-1">{a.summary}</p>
              <p className="text-xs text-slate-500 mt-1">Status: {a.status} · confidence {a.confidence} · sources: {(a.sources || []).length}</p>
              <div className="mt-2 grid gap-2 sm:grid-cols-2 text-xs text-slate-600">
                {Object.entries(a.social_variants || {}).map(([ch, txt]) => <p key={ch}><strong>{ch}:</strong> {txt}</p>)}
              </div>
              {a.status !== "published" && (
                <button data-testid={`approve-article-${a.id}`} className="btn btn-primary py-1.5 px-3 text-xs mt-3"
                  disabled={newsLoading || !!newsPending} onClick={() => publishArticle(a.id)}>
                  {newsPending === a.id ? "Saving publication…" : "Approve and publish with social bundle"}
                </button>
              )}
            </div>
          ))}
          {!newsLoading && !newsError && articles.length === 0 && (
            <p className="mt-4 text-sm text-slate-500">
              No newsroom articles are available.
            </p>
          )}
        </section>
      )}

      {tab === "Consultants" && (
        <section className="mt-8 grid gap-6 lg:grid-cols-2" data-testid="admin-consultants">
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Add a consultant profile</h2>
            <p className="mt-2 text-xs text-slate-500">Consultant profiles are created here only. Regulatory details are mandatory before a consultant can accept regulated immigration bookings.</p>
            <label className="block text-sm mt-4">Name<input data-testid="consultant-name-input" className="field mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="block text-sm mt-3">Title<input data-testid="consultant-title-input" className="field mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></label>
            <label className="block text-sm mt-3">Biography<textarea data-testid="consultant-bio-input" rows={2} className="field mt-1" value={form.biography} onChange={(e) => setForm({ ...form, biography: e.target.value })} /></label>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="block text-sm">Price (pence)<input data-testid="consultant-price-input" type="number" className="field mt-1" value={form.price_pence} onChange={(e) => setForm({ ...form, price_pence: e.target.value })} /></label>
              <label className="block text-sm">Durations (minutes)<input data-testid="consultant-durations-input" className="field mt-1" value={form.durations} onChange={(e) => setForm({ ...form, durations: e.target.value })} /></label>
              <label className="block text-sm">Languages<input data-testid="consultant-languages-input" className="field mt-1" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} /></label>
              <label className="block text-sm">Qualifications<input data-testid="consultant-qualifications-input" className="field mt-1" value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} /></label>
            </div>
            <label className="block text-sm mt-3">Categories
              <select multiple data-testid="consultant-categories-select" className="field mt-1 h-32" value={form.categories}
                onChange={(e) => setForm({ ...form, categories: Array.from(e.target.selectedOptions, (o) => o.value) })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="block text-sm mt-3">Regulatory details (required for regulated immigration)
              <input data-testid="consultant-regulatory-input" className="field mt-1" value={form.regulatory_details} onChange={(e) => setForm({ ...form, regulatory_details: e.target.value })} />
            </label>
            <button data-testid="create-consultant-button" onClick={createConsultant} className="btn btn-primary mt-4 w-full justify-center">Publish consultant profile</button>
          </div>
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Published consultants ({consultants.length})</h2>
            <ul className="mt-3 text-sm space-y-3">
              {consultants.map((c) => (
                <li key={c.id} className="border-t border-slate-200 pt-3" data-testid={`consultant-row-${c.id}`}>
                  <p className="font-semibold">{c.name} — {c.title}</p>
                  <p className="text-xs text-slate-600">£{(c.price_pence / 100).toFixed(2)} · {(c.categories || []).join(", ")}</p>
                  <p className="text-xs text-slate-500">Regulatory details: {c.regulatory_details || "none recorded"}</p>
                  <button data-testid={`deactivate-consultant-${c.id}`} className="btn btn-ghost py-1 px-3 text-xs mt-2"
                    onClick={async () => { await api.delete(`/admin/consultants/${c.id}`); load(); }}>Deactivate</button>
                </li>
              ))}
              {consultants.length === 0 && <li className="text-slate-500">No consultant profiles published yet.</li>}
            </ul>
          </div>
        </section>
      )}

      {tab === "Verification queue" && (
        <section className="mt-8" data-testid="admin-verification-queue">
          {verificationAllowed
            ? <VerificationOps />
            : <div className="card p-6" role="status" data-testid="verification-access-message"><h2 className="font-semibold">Verification queue access is restricted</h2><p className="mt-2 text-sm text-amber-800">Your account does not have the verification analyst or supervisor role. Ask an authorised tenant administrator to assign the appropriate role if you need access.</p><p className="mt-2 text-xs text-slate-500">No verification case data has been requested or displayed.</p></div>}
        </section>
      )}

      {tab === "Notifications" && (
        <section className="mt-8 card p-6" data-testid="admin-notifications">
          <h2 className="text-lg font-semibold">Notification delivery</h2>
          <button data-testid="load-notifications-button" className="btn btn-ghost mt-3" onClick={async () => {
            try {
              const [n, t] = await Promise.all([api.get("/admin/notifications"), api.get("/admin/notifications/templates")]);
              setExtra({ ...extra, notifications: n.data, templates: t.data });
            } catch (e) { setMsg(e?.response?.data?.detail || "Unable to load notifications."); }
          }}>Load delivery report</button>
          {extra.notifications && (
            <>
              <p className="mt-4 text-sm" data-testid="email-channel-status">
                Email channel: <strong>{extra.notifications.email_channel.status}</strong>
                {extra.notifications.email_channel.reason ? ` — ${extra.notifications.email_channel.reason}` : ""}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.entries(extra.notifications.delivery_counts || {}).map(([k, v]) => (
                  <span key={k} className="pill bg-slate-100 text-slate-700">{k.replace(/_/g, " ")}: {v}</span>
                ))}
              </div>
              <ul className="mt-4 text-sm space-y-1 max-h-72 overflow-y-auto">
                {(extra.notifications.items || []).slice(0, 60).map((n) => (
                  <li key={n.id} className="flex justify-between gap-3 border-b border-slate-100 py-1">
                    <span className="truncate">{n.subject}</span>
                    <span className="text-xs text-slate-500 shrink-0">{n.delivery_status?.replace(/_/g, " ")}</span>
                  </li>
                ))}
                {!(extra.notifications.items || []).length && (
                  <li className="text-slate-500">
                    No notification delivery records are available.
                  </li>
                )}
              </ul>
              <p className="mt-4 text-xs text-slate-500">
                {(extra.templates?.templates || []).length} versioned templates are registered. Nothing is
                reported as sent unless the provider accepted it.
              </p>
            </>
          )}
        </section>
      )}

      {tab === "Payments" && (
        <section className="mt-8 card p-6 overflow-x-auto" data-testid="admin-payments">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Transactions{payments ? ` (${payments.total ?? payments.count})` : ""}</h2>
            <button className="btn btn-ghost" onClick={() => loadPayments(payments?.page || 1)}
              disabled={paymentsLoading || Boolean(paymentRetry) || refundSaving || Boolean(refundPayment)} data-testid="refresh-payments">
              {paymentsLoading ? "Loading…" : "Refresh payments"}
            </button>
          </div>
          {paymentsError && <p className="mt-3 text-sm text-red-700" role="alert">{paymentsError} Use Refresh payments to retry.</p>}
          {paymentsLoading && <p className="mt-3 text-sm" role="status">Loading current transactions…</p>}
          {payments && <div className="mt-2 text-sm" data-testid="payment-mode-totals">
            <p>Paid on this page:</p>
            {[{ label: "Live", mode: true }, { label: "Test", mode: false }, { label: "Unknown mode", mode: null }].map(({ label, mode }) => (
              <span className="mr-4" key={label}>{label}: £{((payments.items || []).filter((p) => p.payment_status === "paid"
                && (mode === null ? typeof p.livemode !== "boolean" : p.livemode === mode))
                .reduce((sum, p) => sum + p.amount_pence, 0) / 100).toFixed(2)}</span>
            ))}
          </div>}
          {payments && !payments.items.length && <p className="mt-3 text-sm">No payment transactions are available.</p>}
          {paymentFeedback && <p className="mt-3 text-sm" role="status" aria-live="polite" data-testid="payment-recovery-feedback">{paymentFeedback}</p>}
          <table className="w-full text-sm mt-4">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="py-2">When</th><th>Item</th><th>Description</th><th>Amount</th><th>Payment</th><th>Mode</th><th>Service fulfilment</th><th>Recovery</th><th>Refund request</th>
            </tr></thead>
            <tbody>
              {(payments?.items || []).map((p) => (
                <tr key={p.session_id} className="border-t border-slate-200" data-testid={`payment-row-${p.session_id}`}>
                  <td className="py-2">{new Date(p.created_at).toLocaleString("en-GB")}</td>
                  <td>{p.item_type}</td><td>{p.description}</td>
                  <td>£{(p.amount_pence / 100).toFixed(2)}</td>
                  <td><span className={`pill ${p.payment_status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{p.payment_status}</span></td>
                  <td data-testid={`payment-mode-${p.session_id}`}>{p.livemode === true ? "Live" : p.livemode === false ? "Test" : "Unknown"}</td>
                  <td>{(p.fulfilment_status || "unconfirmed").replace(/_/g, " ")}</td>
                  <td>
                    {canRetryPayments && p.payment_status === "paid" && p.fulfilment_status === "failed"
                      && ["course", "cv_marketing_package", "verification_case", "consultation_booking", "visa_request"].includes(p.item_type) && !p.refund_requested && (
                        <button className="btn btn-ghost" disabled={Boolean(paymentRetry) || refundSaving || Boolean(refundPayment) || paymentsLoading}
                          data-testid={`retry-payment-${p.session_id}`} onClick={() => retryPaymentFulfilment(p)}>
                          {paymentRetry === p.session_id ? "Retrying…" : "Retry service fulfilment"}
                        </button>
                      )}
                  </td>
                  <td>
                    {p.refund_requested ? <span>{(p.refund_status || "requested_pending_provider").replace(/_/g, " ")}</span>
                      : canRetryPayments && p.payment_status === "paid" && (
                        <button className="btn btn-ghost" disabled={Boolean(paymentRetry) || refundSaving || Boolean(refundPayment) || paymentsLoading}
                          data-testid={`request-refund-${p.session_id}`} onClick={() => { setRefundPayment(p); setRefundReason(""); setPaymentFeedback(""); }}>
                          Record refund request
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {payments && <div className="mt-4 flex items-center gap-3" data-testid="payment-pagination">
            <button className="btn btn-ghost" disabled={paymentsLoading || Boolean(paymentRetry) || refundSaving || Boolean(refundPayment) || (payments.page || 1) <= 1}
              onClick={() => loadPayments((payments.page || 1) - 1)}>Previous</button>
            <span className="text-sm">Page {payments.page || 1} · {payments.count} transactions shown</span>
            <button className="btn btn-ghost" disabled={paymentsLoading || Boolean(paymentRetry) || refundSaving || Boolean(refundPayment) || !payments.has_more}
              onClick={() => loadPayments((payments.page || 1) + 1)}>Next</button>
          </div>}
          {refundPayment && canRetryPayments && (
            <form className="mt-6 border-t border-slate-200 pt-4" onSubmit={recordRefundRequest} data-testid="refund-request-form">
              <h3 className="font-semibold">Record refund request</h3>
              <p className="mt-2 text-sm">{refundPayment.description} · £{(refundPayment.amount_pence / 100).toFixed(2)}</p>
              <p className="mt-2 text-sm">This records a request for finance review. It does not send money back; provider-side refund execution is separate.</p>
              <label className="block mt-3 text-sm" htmlFor="refund-reason">Reason</label>
              <textarea id="refund-reason" className="input w-full mt-1" rows={3} maxLength={2000}
                required disabled={refundSaving} value={refundReason} onChange={(event) => setRefundReason(event.target.value)} />
              <div className="mt-3 flex gap-2">
                <button className="btn btn-primary" type="submit" disabled={refundSaving || paymentsLoading || !refundReason.trim()}>
                  {refundSaving ? "Recording…" : "Record request"}
                </button>
                <button className="btn btn-ghost" type="button" disabled={refundSaving} onClick={() => setRefundPayment(null)}>Cancel</button>
              </div>
            </form>
          )}
        </section>
      )}

      {tab === "Data quality" && (
        <section className="mt-8 card p-6" data-testid="admin-data-quality">
          <h2 className="text-lg font-semibold">Sponsor data-quality queue</h2>
          <button data-testid="load-data-quality" className="btn btn-ghost mt-3"
            onClick={async () => setExtra({ ...extra, dq: (await api.get("/sponsors/data-quality/queue")).data })}>
            Load queue
          </button>
          {extra.dq && (
            <div className="mt-4 text-sm">
              <p className="font-semibold">Awaiting manual review: {extra.dq.awaiting_manual_review.length}</p>
              <p className="font-semibold mt-3">Organisation names appearing in more than one location: {extra.dq.possible_duplicate_names.length}</p>
              <ul className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {extra.dq.possible_duplicate_names.slice(0, 50).map((d) => (
                  <li key={d.organisation_name}>{d.organisation_name} — {d.locations} locations</li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-slate-500">{extra.dq.note}</p>
            </div>
          )}
        </section>
      )}

      {tab === "Users" && (
        <section className="mt-8 card p-6 overflow-x-auto" data-testid="admin-users">
          <h2 className="text-lg font-semibold">Users</h2>
          <button data-testid="load-users" className="btn btn-ghost mt-3"
            onClick={async () => setExtra({ ...extra, users: (await api.get("/admin/users")).data })}>
            Load users
          </button>
          {extra.users && (
            <table className="w-full text-sm mt-4">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2">Name</th><th>Email</th><th>Roles</th><th>Email verified</th>
              </tr></thead>
              <tbody>
                {extra.users.items.map((u) => (
                  <tr key={u.id} className="border-t border-slate-200" data-testid={`user-row-${u.id}`}>
                    <td className="py-2">{u.full_name}</td><td>{u.email}</td>
                    <td className="text-xs">{(u.roles || []).join(", ")}</td>
                    <td>{u.email_verified ? "Yes" : "No"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {tab === "Compliance" && (
        <section className="mt-8 card p-6" data-testid="admin-compliance">
          <h2 className="text-lg font-semibold">Items awaiting EuropEdu legal or regulatory approval</h2>
          <button data-testid="load-compliance" className="btn btn-ghost mt-3"
            onClick={async () => setExtra({ ...extra, comp: (await api.get("/compliance/pending-approvals")).data })}>
            Load pending approvals
          </button>
          {extra.comp && (
            <div className="mt-4 text-sm space-y-4">
              <div>
                <p className="font-semibold">Policies awaiting review ({extra.comp.policies_awaiting_review.length})</p>
                <ul className="mt-1 space-y-1">
                  {extra.comp.policies_awaiting_review.map((p) => (
                    <li key={p.slug}>{p.title} v{p.version} — {(p.review_status || "review_required").replace(/_/g, " ")}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold">Declarations awaiting solicitor review ({extra.comp.declarations_awaiting_review.length})</p>
                <ul className="mt-1 space-y-1">
                  {extra.comp.declarations_awaiting_review.map((d, i) => (
                    <li key={i}>{d.title} v{d.version} — {(d.legal_review_status || "review_required").replace(/_/g, " ")}</li>
                  ))}
                </ul>
              </div>
              <p><span className="font-semibold">Visa fee records awaiting official review:</span> {extra.comp.visa_fees_awaiting_official_review}</p>
              <p><span className="font-semibold">Courses without verified accreditation:</span> {extra.comp.courses_without_verified_accreditation.length}</p>
              <p><span className="font-semibold">Regulated advisers requiring regulator confirmation:</span> {extra.comp.regulated_advisers_requiring_regulator_confirmation.length}</p>
            </div>
          )}
        </section>
      )}

      {tab === "Audit logs" && (
        <section className="mt-8 card p-6 overflow-x-auto" data-testid="admin-audit-logs">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold">Audit history</h2>
            <button className="btn btn-ghost" disabled={auditLoading} onClick={() => loadAudit(logs.page || 1)}>Refresh history</button>
          </div>
          {auditLoading && <p role="status" className="mb-3 text-sm">Loading audit history…</p>}
          {auditError && <p role="alert" className="mb-3 text-sm text-red-700">{auditError}</p>}
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">When</th><th>Actor</th><th>Action</th><th>Entity</th></tr></thead>
            <tbody>
              {logs.items.map((l) => (
                <tr key={l.id} className="border-t border-slate-200">
                  <td className="py-2">{new Date(l.created_at).toLocaleString("en-GB")}</td>
                  <td>{l.actor_email}</td><td>{l.action}</td><td>{l.entity}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!auditLoading && !auditError && !logs.items.length && <p className="mt-3 text-sm text-slate-500">No audit events on this page.</p>}
          <div className="mt-4 flex items-center justify-between gap-3 text-sm">
            <button className="btn btn-ghost" disabled={auditLoading || (logs.page || 1) <= 1} onClick={() => loadAudit(logs.page - 1)}>Previous</button>
            <span>{logs.total || 0} events · Page {logs.page || 1} of {Math.max(1, logs.pages || 0)}</span>
            <button className="btn btn-ghost" disabled={auditLoading || (logs.page || 1) >= (logs.pages || 0)} onClick={() => loadAudit(logs.page + 1)}>Next</button>
          </div>
        </section>
      )}

      {tab === "System health" && (
        <div className="mt-8 card p-4">
          <button className="btn btn-ghost" disabled={healthLoading || !!jobRetryPending} onClick={loadHealth}>
            {healthLoading ? "Refreshing system health…" : "Refresh system health"}
          </button>
          {healthLoading && <p role="status" className="mt-2 text-sm">Loading system health and background jobs…</p>}
          {healthError && <p role="alert" className="mt-2 text-sm text-red-700">{healthError}</p>}
          {health && healthError && <p className="mt-2 text-xs text-slate-500">Showing the last successfully loaded information.</p>}
        </div>
      )}
      {tab === "System health" && health && (
        <section className="mt-8 grid gap-6 lg:grid-cols-2" data-testid="admin-health">
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Integrations</h2>
            <ul className="mt-3 text-sm space-y-2">
              {health.integrations.map((i) => (
                <li key={i.key} className="flex justify-between" data-testid={`integration-${i.key}`}>
                  <span>{i.display_name}</span>
                  <span className={`pill ${i.status === "connected" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{i.status.replace(/_/g, " ")}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Background and scheduled jobs</h2>
            {health.worker && <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 p-3" data-testid="worker-health">
              <span className="text-sm">Background worker</span>
              <span className={`pill ${health.worker.healthy ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{health.worker.status}</span>
            </div>}
            {health.worker?.heartbeat_at && <p className="mt-2 text-xs text-slate-500">Last heartbeat: {new Date(health.worker.heartbeat_at).toLocaleString()} · {health.worker.age_seconds}s ago</p>}
            {health.worker?.last_error && <p className="mt-2 text-xs text-red-700">{health.worker.last_error}</p>}
            {health.worker_alert?.incident_open && <p className="mt-2 text-xs font-medium text-red-700">Operational alert sent · incident open since {new Date(health.worker_alert.opened_at).toLocaleString()}</p>}
            {health.worker_alert?.recovered_at && !health.worker_alert?.incident_open && <p className="mt-2 text-xs text-emerald-700">Last worker incident recovered {new Date(health.worker_alert.recovered_at).toLocaleString()}</p>}
            {health.backup && <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 p-3" data-testid="backup-health">
              <span className="text-sm">Database and document backup</span>
              <span className={`pill ${health.backup.healthy ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{health.backup.status}</span>
            </div>}
            {health.backup?.last_run_at && <p className="mt-2 text-xs text-slate-500">Last verified backup: {new Date(health.backup.last_run_at).toLocaleString()} · {health.backup.age_seconds}s ago</p>}
            {health.backup?.storage?.available && <p className="mt-2 text-xs text-slate-500">Backup storage: {health.backup.storage.used_percent}% used · {(health.backup.storage.free_bytes / (1024 ** 3)).toFixed(2)} GB free</p>}
            {health.backup?.last_error && <p className="mt-2 text-xs text-red-700">{health.backup.last_error}</p>}
            <p className="mt-2 text-sm">Queued: {health.background_jobs.queued} · Failed: {health.background_jobs.failed}</p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm" data-testid="background-job-queue">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">Type</th><th>Status</th><th>Attempts</th><th>Last error</th><th>Action</th></tr></thead>
                <tbody>{backgroundQueue.map((job) => <tr key={job.id} className="border-t border-slate-200">
                  <td className="py-3">{(job.type || "unknown").replaceAll("_", " ")}</td>
                  <td><span className={`pill ${job.status === "completed" ? "bg-emerald-50 text-emerald-700" : job.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>{(job.status || "unknown").replaceAll("_", " ")}</span></td>
                  <td>{job.attempts || 0}</td>
                  <td className="max-w-xs truncate" title={job.last_error || ""}>{job.last_error || "—"}</td>
                  <td>{["failed", "blocked_integration_not_connected"].includes(job.status) && ["email", "consultation_reminder"].includes(job.type) && <button type="button" className="btn-secondary" disabled={healthLoading || !!jobRetryPending} onClick={() => retryBackgroundJob(job.id)}>{jobRetryPending === job.id ? "Queuing retry…" : "Retry"}</button>}</td>
                </tr>)}</tbody>
              </table>
              {!healthLoading && !healthError && !backgroundQueue.length && <p className="mt-3 text-sm text-slate-500">No background jobs recorded for this tenant.</p>}
            </div>
            <ul className="mt-3 text-sm space-y-1">
              {health.scheduled_jobs.map((s) => <li key={s.id}>{s.name} · {s.schedule} · {s.status}</li>)}
            </ul>
          </div>
          <div className="card p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold">Feature flags</h2>
            <ul className="mt-3 text-sm space-y-1">
              {flags.map((f) => <li key={f.key} data-testid={`flag-${f.key}`}>{f.key} · enabled for {f.enabled_for}</li>)}
              {flags.length === 0 && (
                <li className="text-slate-500">
                  No tenant-specific feature flags are configured.
                </li>
              )}
            </ul>
          </div>
        </section>
      )}

      {tab === "Message Templates" && (
        <section className="mt-8 space-y-4" data-testid="admin-message-templates">
          <div className="card p-6">
            <h2 className="text-lg font-semibold">WhatsApp message templates</h2>
            {templateBusy && <p role="status" className="mt-2 text-sm text-slate-600">Saving template changes…</p>}
            <p className="mt-2 text-sm text-slate-600">Edit common messages used by staff. Placeholders: {"{name}"}, {"{service}"}, {"{details}"}, {"{documents}"}.</p>
            <button className="btn btn-ghost mt-4" disabled={templateBusy || templatesLoading} onClick={loadMessageTemplates}>{templatesLoading ? "Loading…" : "Refresh templates"}</button>
          </div>
          <div className="card p-6">
            <h2 className="text-lg font-semibold">Create new template</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">Template name<input className="field mt-2 w-full" disabled={templateBusy} value={newTemplate.label} onChange={(e) => setNewTemplate({ ...newTemplate, label: e.target.value })} placeholder="Interview reminder" /></label>
              <label className="text-sm font-medium text-slate-700">Channel<select className="field mt-2 w-full" disabled={templateBusy} value={newTemplate.channel} onChange={(e) => setNewTemplate({ ...newTemplate, channel: e.target.value })}><option value="whatsapp">WhatsApp</option><option value="email">Email</option></select></label>
            </div>
            <label className="mt-4 block text-sm font-medium text-slate-700">Message<textarea className="field mt-2 min-h-32 w-full" disabled={templateBusy} value={newTemplate.body} onChange={(e) => setNewTemplate({ ...newTemplate, body: e.target.value })} placeholder="Hello {name}, ..." /></label>
            <p className="mt-2 text-xs text-slate-500">You can use {`{name}`}, {`{service}`}, {`{details}`} and {`{documents}`} as placeholders.</p>
            <button type="button" className="btn btn-primary mt-4" disabled={templateBusy} onClick={createMessageTemplate}>Create template</button>
          </div>
          <div className="space-y-4">
            {messageTemplates.map((template) => (
              <article key={template.key} className="card p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><h3 className="font-semibold">{template.label}</h3><p className="text-xs text-slate-500">{template.key} · {template.channel}</p></div>
                  <div className="flex gap-2"><button type="button" className="btn btn-primary text-sm" disabled={templateBusy} onClick={() => saveMessageTemplate(template)}>Save template</button>{!template.is_default && <button type="button" className="btn btn-ghost text-sm text-rose-700" disabled={templateBusy} onClick={() => deleteMessageTemplate(template)}>Delete</button>}</div>
                </div>
                <fieldset disabled={templateBusy} className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="block text-sm font-medium text-slate-700">Template name
                    <input className="field mt-2 w-full" maxLength={120} value={template.label} onChange={(e) => editMessageTemplate(template.key, { label: e.target.value })} />
                  </label>
                  <label className="block text-sm font-medium text-slate-700">Channel
                    <select className="field mt-2 w-full" value={template.channel} onChange={(e) => editMessageTemplate(template.key, { channel: e.target.value })}>
                      <option value="whatsapp">WhatsApp</option><option value="email">Email</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <input type="checkbox" checked={template.active !== false} onChange={(e) => editMessageTemplate(template.key, { active: e.target.checked })} />
                    Active template
                  </label>
                </fieldset>
                <p className="mt-2 text-xs text-slate-500">Save template to apply changes. Only active WhatsApp templates appear in the WhatsApp hub.</p>
                <label className="mt-4 block text-sm font-medium text-slate-700">Template message
                  <textarea className="field mt-2 min-h-32 w-full" maxLength={4000} disabled={templateBusy} value={template.body} onChange={(e) => editMessageTemplate(template.key, { body: e.target.value })} />
                </label>
              </article>
            ))}
            {!messageTemplates.length && <p className="card p-6 text-sm text-slate-500">Click Refresh templates to load the templates.</p>}
          </div>
        </section>
      )}
      {tab === "WhatsApp Communications" && (
        <section className="mt-8 card p-6" data-testid="admin-whatsapp-communications">
          <h2 className="text-lg font-semibold">WhatsApp Communication Hub</h2>
          <p className="mt-2 text-sm text-slate-600">Prepare and review messages, register incoming enquiries, and keep a manual communication record without WhatsApp API charges.</p>
          <div className="mt-4 flex flex-wrap gap-3"><a href="/communications/whatsapp" className="btn btn-primary inline-flex">Open WhatsApp Communication Hub</a><a href="/communications/whatsapp/operations" className="btn btn-ghost inline-flex">Open Inbox & Follow-ups</a></div>
        </section>
      )}

      {tab === "Candidates" && (
        <section className="mt-8 card p-6" data-testid="admin-candidates">
          <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="text-lg font-semibold">Candidate directory{candidateLoading ? "" : ` (${candidates.length})`}</h2><p className="mt-2 text-sm text-slate-600">Search by name, email, phone/WhatsApp, candidate ID, application ID, city or country.</p></div><button className="btn btn-ghost" onClick={loadCandidates} disabled={candidateLoading}>{candidateLoading ? "Loading candidates…" : "Refresh candidates"}</button></div>
          <AdminCandidateSearch search={candidateSearch} setSearch={setCandidateSearch} city={candidateCity} setCity={setCandidateCity} country={candidateCountry} setCountry={setCandidateCountry} active={candidateActiveFilters} onSearch={loadCandidates} onClear={clearCandidateSearch} />
          <div className="mt-5 overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="py-2">Candidate</th><th>Email</th><th>Phone / WhatsApp</th><th>Location</th><th>Applications</th></tr></thead><tbody>{candidates.map((candidate) => <AdminCandidateRow key={candidate.id} candidate={candidate} onOpen={openCandidate} />)}</tbody></table>{candidateLoading ? <p className="mt-4 text-sm text-slate-500">Loading candidate records…</p> : !candidates.length && <p className="mt-4 text-sm text-slate-500">No candidates match the current search.</p>}</div>
        </section>
      )}
        {selectedCandidate && <AdminCandidateDetails detail={selectedCandidate} edit={candidateEdit} setEdit={setCandidateEdit} onSaved={refreshCandidate} onClose={() => setSelectedCandidate(null)} />}
      {tab === "Study applications" && (
        <section className="mt-8 card p-6" data-testid="admin-study-applications">
          <h2 className="text-lg font-semibold">Study applications ({studyApplications.length})</h2>
          <p className="mt-2 text-sm text-slate-600">Review candidate contact details, application notes and optional biodata attachments before progressing the enquiry.</p>
          <div className="mt-5 space-y-4">
            {studyApplications.map((a) => {
              let details = {};
              try { details = JSON.parse(a.message || "{}"); } catch (e) { details = { details: a.message }; }
              return <article key={a.id} className="rounded-xl border border-slate-200 p-4 transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100">
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-slate-950">{a.course_title}</h3>
                    <p className="text-sm text-slate-600">{a.provider_name} · {a.country}</p>
                  </div>
                  <span className="pill bg-amber-50 text-amber-800">{a.status}</span>
                </div>
                <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                  <p><strong>Name:</strong> {details.contact?.fullName || "Not recorded"}</p>
                  <p><strong>Email:</strong> {details.contact?.email || "Not recorded"}</p>
                  <p><strong>Contact:</strong> {details.contact?.phone || "Not recorded"}</p>
                  <p><strong>WhatsApp:</strong> {details.contact?.whatsapp || "Not recorded"}</p>
                </div>
                <p className="mt-3 text-sm text-slate-700"><strong>Details:</strong> {details.details || "Not recorded"}</p>
                {a.review_note && <p className="mt-2 text-sm text-blue-900"><strong>Latest review note:</strong> {a.review_note}</p>}
                <p className="mt-3 text-xs text-slate-500"><strong>Attachments:</strong> {a.attachments?.length ? a.attachments.map((x) => `${x.original_filename} (${x.malware_scan_status})`).join(", ") : "None attached"}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[
                    ["under_review", "Start review"],
                    ["information_requested", "Request information"],
                    ["progressing", "Progress application"],
                    ["closed", "Close application"],
                  ].map(([status, label]) => (
                    <button key={status} className="btn btn-ghost px-3 py-1.5 text-xs" onClick={() => {
                      setReviewAction({ application: a, status, label, details });
                      setReviewNote(a.review_note || "");
                    }}>{label}</button>
                  ))}
                </div>
              </article>;
            })}
            {studyApplications.length === 0 && <p className="text-sm text-slate-500">No study applications have been submitted.</p>}
          </div>
        </section>
      )}

      {tab === "Programme reviews" && (
        <section className="mt-8 card p-6" data-testid="admin-programme-reviews">
          <div className="flex flex-wrap items-start justify-between gap-4"><div><h2 className="text-lg font-semibold">Programme review queue</h2>
          <p className="mt-2 text-sm text-slate-600">Review submitted assessments before any candidate-facing decision.</p></div><span className="pill bg-blue-50 text-blue-800">{programmeCases.length} case{programmeCases.length === 1 ? "" : "s"}</span></div>
          <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Filter programme cases">{["all", "awaiting_senior_review", "request_information", "development_required", "proceed_to_case", "closed"].map((status) => <button type="button" key={status} onClick={() => setProgrammeStatusFilter(status)} className={`pill transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${programmeStatusFilter === status ? "bg-[#1e3a8a] text-white" : "bg-slate-100 text-slate-700 hover:bg-blue-50 hover:text-blue-900"}`}>{status === "all" ? "All cases" : status.replaceAll("_", " ")}</button>)}</div>
          <div className="mt-5 space-y-3">
            {programmeCases.length === 0 && <p className="text-sm text-slate-500">No programme cases awaiting review.</p>}
            {programmeCases.filter((c) => programmeStatusFilter === "all" || c.status === programmeStatusFilter).map((c) => (
              <article key={c.id} className="rounded-xl border border-slate-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div><h3 className="font-semibold">{c.lead?.full_name || "Unnamed candidate"}</h3><p className="text-sm text-slate-600">{c.lead?.email} · {c.lead?.country}</p></div>
                  <span className="pill bg-amber-50 text-amber-800">{c.status}</span>
                </div>
                <p className="mt-2 text-xs text-slate-500">{c.programme_slug} · terms {c.terms_version || "not recorded"}</p>
                <button type="button" className="btn btn-ghost mt-3 inline-flex text-sm" onClick={() => openProgrammeReview(c.id)}>Open review pack</button>
              </article>
            ))}
            {programmeCases.length > 0 && !programmeCases.some((c) => programmeStatusFilter === "all" || c.status === programmeStatusFilter) && <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No cases match this status filter.</p>}
          </div>
        </section>
      )}
      {programmeReview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 p-4 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="programme-review-pack-title">
          <section className="my-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4"><div><p className="overline">Programme review pack</p><h2 id="programme-review-pack-title" className="mt-2 text-2xl font-semibold">{programmeReview.candidate?.full_name || "Candidate review"}</h2><p className="mt-1 text-sm text-slate-600">{programmeReview.candidate?.email} · {programmeReview.candidate?.country}</p></div><button type="button" className="btn btn-ghost" onClick={() => { setProgrammeReview(null); setProgrammeReviewHistory([]); }}>Close</button></div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2"><div className="rounded-xl bg-slate-50 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Status</p><p className="mt-1 font-semibold">{programmeReview.status}</p></div><div className="rounded-xl bg-slate-50 p-4"><p className="text-xs uppercase tracking-wider text-slate-500">Programme</p><p className="mt-1 font-semibold">{programmeReview.programme?.slug} · {programmeReview.programme?.version}</p></div></div>
            <div className="mt-5 rounded-xl border border-slate-200 p-4"><h3 className="font-semibold">Review decision</h3><textarea className="field mt-3 min-h-24 w-full" value={programmeReviewNote} onChange={(e) => setProgrammeReviewNote(e.target.value)} placeholder="Add an internal note, or explain information required from the candidate." /><div className="mt-3 flex flex-wrap gap-2"><button type="button" className="btn btn-primary text-sm" disabled={programmeReviewSaving} onClick={() => updateProgrammeReview("proceed_to_case")}>Progress to case</button><button type="button" className="btn btn-ghost text-sm" disabled={programmeReviewSaving} onClick={() => updateProgrammeReview("development_required")}>Development required</button><button type="button" className="btn btn-ghost text-sm" disabled={programmeReviewSaving} onClick={() => updateProgrammeReview("request_information")}>Request information</button><button type="button" className="btn btn-ghost text-sm text-rose-700" disabled={programmeReviewSaving} onClick={() => updateProgrammeReview("closed")}>Close case</button></div></div>
            <div className="mt-5 rounded-xl border border-slate-200 p-4"><h3 className="font-semibold">Submitted documents</h3><div className="mt-2 space-y-2">{programmeReview.documents?.length ? programmeReview.documents.map((file) => <div key={file.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50 p-3 text-sm"><span>{file.original_filename} · {file.malware_scan_status}</span><button type="button" className="text-xs text-blue-700 underline disabled:text-slate-400" disabled={file.malware_scan_status !== "clean" || file.quarantine_status !== "released"} onClick={() => downloadProgrammeDocument(file)}>Open document</button></div>) : <p className="mt-2 text-sm text-slate-500">No documents submitted.</p>}</div></div>
            <div className="mt-5 space-y-4 text-sm"><div><h3 className="font-semibold">Initial readiness result</h3><pre className="mt-2 max-h-48 overflow-auto rounded-xl bg-slate-50 p-4 whitespace-pre-wrap">{JSON.stringify(programmeReview.readiness || {}, null, 2)}</pre></div><div><h3 className="font-semibold">Evidence gaps</h3><p className="mt-2 rounded-xl bg-amber-50 p-4">{programmeReview.evidence_gaps?.length ? programmeReview.evidence_gaps.join(", ") : "None recorded"}</p></div><div><h3 className="font-semibold">Founder authenticity flags</h3><p className="mt-2 rounded-xl bg-slate-50 p-4">{programmeReview.founder_authenticity_flags?.length ? programmeReview.founder_authenticity_flags.join(", ") : "None recorded"}</p></div><div><h3 className="font-semibold">Integrity flags</h3><p className="mt-2 rounded-xl bg-slate-50 p-4">{programmeReview.integrity_flags?.length ? programmeReview.integrity_flags.join(", ") : "None recorded"}</p></div><div><h3 className="font-semibold">Document checklist</h3><div className="mt-2 space-y-2">{programmeReview.document_requirements?.length ? programmeReview.document_requirements.map((requirement) => <div key={requirement.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50 p-3"><span>{requirement.title}</span><select aria-label={`Status for ${requirement.title}`} className="field py-1 text-xs" value={requirement.status} onChange={(event) => updateDocumentRequirement(requirement.id, event.target.value)}><option value="requested">requested</option><option value="received">received</option><option value="approved">approved</option><option value="rejected">rejected</option><option value="missing">missing</option></select></div>) : <p className="mt-2 rounded-xl bg-slate-50 p-4">No document requirements recorded.</p>}</div></div><div><h3 className="font-semibold">Document review history</h3><div className="mt-2 space-y-2">{programmeReviewHistory.length ? programmeReviewHistory.map((event) => <div key={event.id} className="rounded-xl bg-slate-50 p-3"><p className="text-xs text-slate-500">{event.created_at ? new Date(event.created_at).toLocaleString("en-GB") : "Time not recorded"} · {event.action || event.event || "Audit event"}</p><p className="mt-1">{event.metadata?.status || event.details?.status || "Recorded review activity"}</p></div>) : <p className="mt-2 rounded-xl bg-slate-50 p-4">No document review history recorded.</p>}</div></div><div><h3 className="font-semibold">Answers</h3><pre className="mt-2 max-h-48 overflow-auto rounded-xl bg-slate-50 p-4 whitespace-pre-wrap">{JSON.stringify(programmeReview.answers || {}, null, 2)}</pre></div></div>
          </section>
        </div>
      )}
      {reviewAction && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/50 p-4 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="study-review-title">
          <form className="my-4 max-h-[calc(100vh-2rem)] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:my-0 sm:max-h-[90vh]" onSubmit={async (e) => {
            e.preventDefault();
            if (reviewPending.current) return;
            if (reviewAction.status === "information_requested" && reviewNote.trim().length < 5) {
              setReviewError("Enter a message of at least five characters for the candidate."); return;
            }
            reviewPending.current = true; setReviewSaving(true); setReviewError("");
            try {
              const { data } = await api.patch(`/staff/study/applications/${reviewAction.application.id}`, { status: reviewAction.status, note: reviewNote.trim() || null });
              setStudyApplications((items) => items.map((item) => item.id === data.id ? data : item));
              setMsg(data.warning || `Application marked: ${reviewAction.label.toLowerCase()}.`);
              setReviewAction(null); setReviewNote("");
            } catch (error) {
              setReviewError(applicationErrorMessage(error, "Unable to save this review. Please try again."));
            } finally { reviewPending.current = false; setReviewSaving(false); }
          }}>
            <p className="overline">Study application review</p>
            <h2 id="study-review-title" className="mt-2 text-2xl font-semibold text-slate-950">{reviewAction.label}</h2>
            <p className="mt-2 text-sm text-slate-600">{reviewAction.application.course_title} · {reviewAction.application.provider_name}</p>
            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
              <p><strong>Candidate:</strong> {reviewAction.details.contact?.fullName || "Not recorded"}</p>
              <p className="mt-1"><strong>Email:</strong> {reviewAction.details.contact?.email || "Not recorded"}</p>
              <p className="mt-1"><strong>WhatsApp:</strong> {reviewAction.details.contact?.whatsapp || "Not recorded"}</p>
            </div>
            <label className="mt-5 block">
              <span className="text-sm font-semibold text-slate-900">{reviewAction.status === "information_requested" ? "Message to candidate" : "Internal review note"}{reviewAction.status === "information_requested" && <span className="text-rose-600"> *</span>}</span>
              <textarea disabled={reviewSaving} className="field mt-2 min-h-32 w-full" value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder={reviewAction.status === "information_requested" ? "Explain exactly what information or document is required." : "Add context for the next staff member."} required={reviewAction.status === "information_requested"} />
              <span className="mt-1 block text-xs text-slate-500">{reviewAction.status === "information_requested" ? "This message will be sent as an in-app notification." : "This note is retained in the staff review record."}</span>
            </label>
            {reviewError && <p role="alert" className="mt-4 text-sm text-red-700">{reviewError}</p>}
            <div className="mt-6 flex justify-end gap-3">
              <button disabled={reviewSaving} type="button" className="btn btn-ghost" onClick={() => setReviewAction(null)}>Cancel</button>
              <button disabled={reviewSaving} type="submit" className="btn btn-primary">{reviewSaving ? "Saving review…" : "Save and continue"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

